import api, { handleApiResponse, handleApiError } from './api';

class RoleService {
  // Get all roles
  async getRoles() {
    try {
      const response = await api.get('/roles');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get single role by ID
  async getRole(roleId) {
    try {
      const response = await api.get(`/roles/${roleId}`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Create new role
  async createRole(roleData) {
    try {
      const response = await api.post('/roles', roleData);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Update existing role
  async updateRole(roleId, roleData) {
    try {
      const response = await api.put(`/roles/${roleId}`, roleData);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Delete role
  async deleteRole(roleId) {
    try {
      const response = await api.delete(`/roles/${roleId}`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Toggle role status (active/inactive)
  async toggleRoleStatus(roleId) {
    try {
      const response = await api.patch(`/roles/${roleId}/toggle-status`);
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Get available permissions
  async getAvailablePermissions() {
    try {
      const response = await api.get('/roles/permissions');
      return handleApiResponse(response);
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  }

  // Validate role data
  validateRoleData(roleData) {
    const errors = {};

    // Name validation
    if (!roleData.name || roleData.name.trim().length === 0) {
      errors.name = 'Role name is required';
    } else if (roleData.name.length > 255) {
      errors.name = 'Role name must be less than 255 characters';
    }

    // Display name validation
    if (roleData.display_name && roleData.display_name.length > 255) {
      errors.display_name = 'Display name must be less than 255 characters';
    }

    // Description validation
    if (roleData.description && roleData.description.length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }

    // Staff level validation - KEEP ONLY THE POSITIVE VALIDATION
    if (this.isStaffRole(roleData.name) && !roleData.staff_level) {
      errors.staff_level = 'Staff level is required for roles containing "staff" in the name';
    }

    if (roleData.staff_level && ![1, 2, 3].includes(parseInt(roleData.staff_level))) {
      errors.staff_level = 'Staff level must be 1, 2, or 3';
    }

    // Permissions validation
    if (roleData.permissions && !Array.isArray(roleData.permissions)) {
      errors.permissions = 'Permissions must be an array';
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }

  // Check if role name indicates it's a staff role
  isStaffRole(roleName) {
    return roleName && roleName.toLowerCase().includes('staff');
  }

  // Get default permissions for staff roles based on level
  getDefaultStaffPermissions(staffLevel) {
    const baseStaffPermissions = [
      'tickets.view-assigned',
      'tickets.comment',
      'tickets.respond',
      'tickets.update',
      'categories.view',
      'dashboard.view-agent',
      'notifications.view',
      'notifications.update'
    ];

    // Level-specific permissions
    const levelPermissions = {
      1: [...baseStaffPermissions], // Level 1: Basic permissions only
      2: [...baseStaffPermissions, 'tickets.forward', 'tickets.escalate'], // Level 2: Can forward and escalate
      3: [...baseStaffPermissions, 'tickets.forward', 'tickets.escalate', 'tickets.assign', 'tickets.solve'] // Level 3: Senior permissions
    };

    return levelPermissions[staffLevel] || baseStaffPermissions;
  }

  // Check if permission is escalation-related
  isEscalationPermission(permissionName) {
    return permissionName.includes('escalate') || 
           permissionName.includes('forward') || 
           permissionName.includes('escalation');
  }

  // Format role data for display
  formatRole(role) {
    return {
      id: role.id,
      name: role.name,
      displayName: role.display_name || role.name,
      description: role.description,
      staffLevel: role.staff_level,
      isActive: role.is_active !== false,
      permissions: role.permissions || [],
      usersCount: role.users_count || 0,
      users: role.users || [],
      isStaffRole: this.isStaffRole(role.name),
      createdAt: role.created_at,
      updatedAt: role.updated_at
    };
  }

  // Format permissions grouped by category
  formatPermissions(permissions) {
    const grouped = {};
    
    Object.keys(permissions).forEach(category => {
      grouped[category] = permissions[category].map(permission => ({
        id: permission.id,
        name: permission.name,
        displayName: permission.display_name,
        description: permission.description,
        category: category,
        isEscalationRelated: this.isEscalationPermission(permission.name)
      }));
    });

    return grouped;
  }

  // Get staff level options
  getStaffLevelOptions() {
    return [
      { value: 1, label: 'Level 1 Staff', description: 'Basic staff permissions (respond, comment)' },
      { value: 2, label: 'Level 2 Staff', description: 'Intermediate staff permissions (+ escalate, forward)' },
      { value: 3, label: 'Level 3 Staff', description: 'Advanced staff permissions (+ assign, solve)' }
    ];
  }

  // Get role type options
  getRoleTypeOptions() {
    return [
      { value: 'customer', label: 'Customer', description: 'External customers' },
      { value: 'agent', label: 'Support Agent', description: 'Customer support agents' },
      { value: 'staff', label: 'Staff', description: 'Internal staff members' },
      { value: 'admin', label: 'Administrator', description: 'System administrators' }
    ];
  }

  // Filter roles by type
  filterRolesByType(roles, type) {
    if (!type || type === 'all') return roles;
    
    return roles.filter(role => {
      const roleName = role.name.toLowerCase();
      switch (type) {
        case 'customer':
          return roleName.includes('customer') || roleName.includes('user');
        case 'agent':
          return roleName.includes('agent') || roleName.includes('support');
        case 'staff':
          return this.isStaffRole(role.name);
        case 'admin':
          return roleName.includes('admin') || roleName.includes('super');
        default:
          return true;
      }
    });
  }

  // Filter roles by status
  filterRolesByStatus(roles, status) {
    if (!status || status === 'all') return roles;
    
    return roles.filter(role => {
      if (status === 'active') return role.isActive;
      if (status === 'inactive') return !role.isActive;
      return true;
    });
  }

  // Search roles by name or description
  searchRoles(roles, searchTerm) {
    if (!searchTerm || searchTerm.trim().length === 0) return roles;
    
    const term = searchTerm.toLowerCase().trim();
    
    return roles.filter(role => 
      role.name.toLowerCase().includes(term) ||
      role.displayName.toLowerCase().includes(term) ||
      (role.description && role.description.toLowerCase().includes(term))
    );
  }

  // Sort roles by specified field
  sortRoles(roles, sortBy = 'name', sortOrder = 'asc') {
    return [...roles].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'usersCount':
          aValue = a.usersCount;
          bValue = b.usersCount;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'staffLevel':
          aValue = a.staffLevel || 0;
          bValue = b.staffLevel || 0;
          break;
        default:
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
      }

      if (sortOrder === 'desc') {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
    });
  }

  // Get permission categories for organization
  getPermissionCategories() {
    return {
      'tickets': 'Ticket Management',
      'users': 'User Management', 
      'roles': 'Role Management',
      'categories': 'Category Management',
      'chat': 'Chat & Communication',
      'dashboard': 'Dashboard Access',
      'escalation': 'Escalation Management',
      'customers': 'Customer Management',
      'config': 'System Configuration',
      'notifications': 'Notification Management'
    };
  }

  // Check if user can perform action on role
  canPerformAction(user, action, role = null) {
    if (!user || !user.permissions) return false;

    const permissions = user.permissions;
    
    switch (action) {
      case 'view':
        return permissions.includes('roles.view');
      case 'create':
        return permissions.includes('roles.create');
      case 'update':
        return permissions.includes('roles.update');
      case 'delete':
        return permissions.includes('roles.delete') && 
               (!role || !role.users || role.users.length === 0);
      default:
        return false;
    }
  }

  // Get staff level badge color
  getStaffLevelBadgeColor(staffLevel) {
    const colors = {
      1: 'bg-green-100 text-green-800',
      2: 'bg-blue-100 text-blue-800', 
      3: 'bg-purple-100 text-purple-800'
    };
    return colors[staffLevel] || 'bg-gray-100 text-gray-800';
  }

  // Get permission count by category for a role
  getPermissionCountByCategory(permissions, category) {
    return permissions.filter(p => p.startsWith(category + '.')).length;
  }
}

export default new RoleService();